import type { SendEventParams } from '@/utils/sendEvent';
import { EventData, PixelResponse } from '../../types';
import { BaseTracker } from './BaseTracker';

export interface ISearchData {
  searchTerm: string;
  category?: string;
  resultsCount?: number;
  filters?: Record<string, unknown>;
}

/**
 * Tracker para eventos de busca
 */
export class SearchTracker extends BaseTracker {
  public getPayload(data: ISearchData): SendEventParams {
    this.validateRequiredParams({ searchTerm: data.searchTerm }, [
      'searchTerm',
    ]);
    const { searchTerm, category, resultsCount, filters } = data;
    return {
      category: 'ENGAGEMENT',
      type: 'SEARCH',
      visitorId: this.visitorId,
      eventData: {
        searchTerm,
        category,
        resultsCount,
        filters,
      },
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
    };
  }

  /**
   * Rastreia uma busca
   */
  public async track({
    searchTerm,
    category,
    resultsCount,
    filters,
  }: ISearchData): Promise<PixelResponse> {
    this.validateRequiredParams({ searchTerm }, ['searchTerm']);
    return this.sendEvent(
      this.getPayload({ searchTerm, category, resultsCount, filters })
    );
  }

  /**
   * Rastreia busca com filtros
   */
  public async trackWithFilters(
    searchData: ISearchData,
    customFilters: Record<string, unknown>
  ): Promise<PixelResponse> {
    this.validateRequiredParams({ searchTerm: searchData.searchTerm }, [
      'searchTerm',
    ]);

    return this.sendEvent({
      category: 'ENGAGEMENT',
      type: 'SEARCH',
      visitorId: this.visitorId,
      eventData: {
        searchTerm: searchData.searchTerm,
        category: searchData.category,
        resultsCount: searchData.resultsCount,
        filters: {
          ...searchData.filters,
          ...customFilters,
        },
      },
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
    });
  }
}

// Função de conveniência para compatibilidade com código existente
export const search = ({
  searchTerm,
  category,
  resultsCount,
  filters,
}: ISearchData): EventData => {
  return {
    event_name: 'SEARCH',
    event_category: 'ENGAGEMENT',
    custom_parameters: {
      search_term: searchTerm,
      category,
      results_count: resultsCount,
      filters,
    },
  };
};
